import { useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useLoaderData, useNavigate, useRevalidator, useSearchParams, type LoaderFunctionArgs } from 'react-router-dom';
import { Button, EmptyState, Modal, Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@immo/ui-kit';
import type {
  ApiCallListItem,
  ApiKeyRequestOutcomeDto,
  GetAdminApiKeyResponse,
  ListApiKeyRequestsQuery,
} from '@immo/api-contract';
import { apiClient } from '@/lib/auth/api-client';
import { RequirePermission } from '@/lib/auth/permissions';
import { buildOperatorNameMap, operatorName, type OperatorNameMap } from '@/lib/api-keys/operator-name';
import { usePermissionLabel, usePermissionLiteral } from '@/lib/permissions/catalogue';
import { useTranslations } from '@/i18n/use-translations';
import { BreadcrumbLeading, ColumnLabel } from '@/components/layout/page-leading';
import { PageTopbar } from '@/components/layout/topbar-slots';
import { ClockIcon, KeyIcon, TagIcon, TextIcon } from '@/components/layout/icons';
import { ResourceHeader } from '@/components/resource-header';
import { EditApiKeyDialog } from '@/pages/api-keys/edit-api-key-dialog';
import { FilterBar, FilterPill } from '@/components/filter-bar';
import { CommitOnBlurInput } from '@/components/commit-on-blur-input';
import { ARIA_SORT, SortableHeader, useTableSort, type SortValue } from '@/components/sortable-table';
import { ALL_WORKSPACES_QUERY } from '@/lib/test-workspaces/include-test-workspaces';

// The per-key trail's outcome filter pills. `auth-failed` is deliberately absent:
// an entry with no key identity can never appear in a key's trail, so the pill
// could never match — the dead affordance that produced IMM-441 and IMM-446. It
// lives on the global call log (IMM-457.2), where it works. The `auth-failed`
// i18n label is kept for any historical detail rendering.
const OUTCOMES: ReadonlyArray<ApiKeyRequestOutcomeDto> = [
  'success',
  'denied',
  'rate-limited',
  'client-error',
  'server-error',
];

// Sort only: the calls table on a detail page has fixed columns (IMM-1074.7).
const REQUEST_HEADS = [
  { field: 'occurredAt', labelKey: 'occurred-at', icon: ClockIcon, className: 'w-[170px]' },
  { field: 'endpoint', labelKey: 'endpoint', icon: TextIcon, className: undefined },
  { field: 'status', labelKey: 'status', icon: TagIcon, className: 'w-[90px]' },
  { field: 'outcome', labelKey: 'outcome', icon: TagIcon, className: 'w-[170px]' },
] as const;
type RequestField = (typeof REQUEST_HEADS)[number]['field'];

// A row with its outcome label resolved, so the sort key stays module-level and stable.
type RequestRow = { readonly request: ApiCallListItem; readonly outcomeLabel: string };

function requestSortKey(row: RequestRow, field: RequestField): SortValue {
  switch (field) {
    case 'occurredAt':
      return Date.parse(row.request.occurredAt);
    case 'endpoint':
      return `${row.request.method} ${row.request.endpoint}`;
    case 'status':
      return row.request.statusCode;
    case 'outcome':
      // The replay mark after the label annotates the cell; it is not the column's value.
      return row.outcomeLabel;
  }
}

type LoaderData = {
  readonly key: GetAdminApiKeyResponse;
  readonly requests: ReadonlyArray<ApiCallListItem>;
  readonly nextCursor: string | null;
  // id → operator name, resolved client-side from the admin list (issuedBy /
  // revokedByAdmin are bare admin ids — no server-side name join).
  readonly operatorNames: OperatorNameMap;
};

// The request filters (AC 18), read from the URL search params so the loader and
// the "load more" fetch agree. The key id is in the path, never a query param.
function requestQueryFromParams(searchParams: URLSearchParams): ListApiKeyRequestsQuery {
  const query: ListApiKeyRequestsQuery = {};
  const outcome = searchParams.get('outcome');
  const endpoint = searchParams.get('endpoint');
  const from = searchParams.get('from');
  const to = searchParams.get('to');

  if (outcome) query.outcome = outcome as ApiKeyRequestOutcomeDto;

  if (endpoint) query.endpoint = endpoint;

  if (from) query.from = from;

  if (to) query.to = to;

  return query;
}

export async function apiKeyDetailLoader({ params, request }: LoaderFunctionArgs): Promise<LoaderData> {
  const id = params['id'];

  if (typeof id !== 'string' || id.length === 0) {
    throw new Response('Missing API key id', { status: 404 });
  }

  const query = requestQueryFromParams(new URL(request.url).searchParams);

  const [keyResult, requestsResult, adminsResult] = await Promise.all([
    apiClient.adminApiKeys.get({ pathParams: { id } }),
    // The request trail is supplementary — a rejection degrades to an empty
    // panel rather than dropping the whole page into the error boundary.
    // Pinned to this key on the cross-key trail — the key-scoped route it used
    // to call is retired (IMM-494.2) and `?apiKeyId=` is the same read. Asked
    // for test workspaces explicitly: a detail page is never gated by the
    // operator's the test-workspace filter filter (IMM-593.2).
    apiClient.adminApiCalls
      .list({ query: { ...query, apiKeyId: id, credentialClass: 'api-key', ...ALL_WORKSPACES_QUERY } })
      .catch(() => null),
    // The admin list resolves issuedBy / revokedByAdmin ids to names; a rejection
    // degrades to the generic operator label rather than failing the page.
    apiClient.adminAdmins.list().catch(() => null),
  ]);

  if (!keyResult.ok) {
    throw new Response('Failed to load API key', { status: keyResult.status });
  }

  return {
    key: keyResult.body,
    requests: requestsResult?.ok ? requestsResult.body.data : [],
    nextCursor: requestsResult?.ok ? requestsResult.body.nextCursor : null,
    operatorNames: buildOperatorNameMap(adminsResult?.ok ? adminsResult.body.data : []),
  };
}

// `datetime-local` yields `YYYY-MM-DDTHH:mm`; convert to a full ISO string for
// the contract's `z.iso.datetime()`. Empty → null (param removed).
function toIso(localValue: string): string | null {
  if (localValue === '') return null;
  const date = new Date(localValue);

  return Number.isNaN(date.getTime()) ? null : date.toISOString();
}

// `<input type="datetime-local">` wants `YYYY-MM-DDTHH:mm` (local time), not the
// stored ISO. Convert back for the controlled value.
function isoToLocalInput(iso: string | null): string {
  if (iso === null) return '';
  const date = new Date(iso);

  if (Number.isNaN(date.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');

  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

function labelFor(name: string | null, id: string | null, fallback: string): string {
  if (name !== null && name !== '') return name;

  if (id !== null && id !== '') return id;

  return fallback;
}

function SectionHeading({ children }: { children: React.ReactNode }) {
  return <h2 className="text-sm font-semibold uppercase tracking-wide text-text-tertiary">{children}</h2>;
}

export function ApiKeyDetailPage() {
  const t = useTranslations('pages.admin.api-keys');
  const tDetail = useTranslations('pages.admin.api-keys.detail');
  const tRequests = useTranslations('pages.admin.api-keys.requests');
  const tTable = useTranslations('pages.admin.shell.table');
  const tRevoke = useTranslations('pages.admin.api-keys.revoke');
  const tEdit = useTranslations('pages.admin.api-keys.edit');
  const tReplay = useTranslations('pages.admin.api-keys.replay');
  const intl = useIntl();
  const permissionLabel = usePermissionLabel();
  const permissionIdentifier = usePermissionLiteral();
  const navigate = useNavigate();
  const revalidator = useRevalidator();
  const { key, requests, nextCursor, operatorNames } = useLoaderData() as LoaderData;

  /**
   * The one sentence stating everything an admin API key reaches (IMM-587.7).
   *
   * Names the bound operator and the scope — the two things the KEY itself pins.
   * Deliberately NOT its capabilities: an admin API key holds no permissions of its
   * own, so a hardcoded list of what it can do would be wrong the moment its
   * operator's role is edited, which is the staleness the live-resolution model
   * exists to avoid.
   *
   * Gated on the kind, not on the presence of a bound operator: an operator on a
   * non-admin API key would be a data defect, and rendering the sentence for it
   * would present that defect as normal.
   */
  const reachSentence = ((): string | null => {
    if (key.kind !== 'admin' || key.scope === null) return null;

    // Degrades to the identifier rather than a gap: this sentence is about blast
    // radius, which matters most when something is already odd.
    const name = key.adminId === null ? null : operatorName(key.adminId, operatorNames, key.adminId);

    if (name === null) return null;

    return key.scope.kind === 'ALL'
      ? tDetail('reach-all', { operatorName: name })
      : tDetail('reach-selected', { operatorName: name, count: key.scope.workspaceIds.length });
  })();
  const [searchParams, setSearchParams] = useSearchParams();

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [revoking, setRevoking] = useState(false);
  const [revokeError, setRevokeError] = useState<string | null>(null);
  const [editOpen, setEditOpen] = useState(false);

  const closeConfirm = () => {
    if (revoking) return;
    setConfirmOpen(false);
    setRevokeError(null);
  };

  const confirmRevoke = async () => {
    setRevoking(true);
    setRevokeError(null);

    try {
      const result = await apiClient.adminApiKeys.revoke({ pathParams: { id: key.id }, body: {} });

      if (result.ok) {
        setConfirmOpen(false);
        revalidator.revalidate();

        return;
      }

      setRevokeError(result.status === 403 ? tRevoke('forbidden-error') : tRevoke('generic-error'));
    } catch {
      setRevokeError(tRevoke('generic-error'));
    } finally {
      setRevoking(false);
    }
  };

  // "Ausgestellt von": the resolved operator for an admin-issued key, else the
  // customer marker (null issuedBy — a customer-created or pre-feature key).
  const issuedByLabel =
    key.issuedBy === null ? t('customer-created') : operatorName(key.issuedBy, operatorNames, t('operator-fallback'));

  // Revoker (shown alongside the revocation timestamp): an admin revoke sets
  // revokedByAdmin (→ operator name, precedence); a customer revoke sets
  // revokedBy (→ „Vom Kunden widerrufen"); an active key sets neither.
  const revokedByLabel =
    key.revokedByAdmin !== null
      ? operatorName(key.revokedByAdmin, operatorNames, t('operator-fallback'))
      : key.revokedBy !== null
        ? t('customer-revoked')
        : null;

  // Accumulate request pages client-side: the loader gives page 1 (and resets
  // the list whenever the filters change), "Mehr laden" appends the next keyset
  // page.
  const [rows, setRows] = useState<ReadonlyArray<ApiCallListItem>>(requests);
  const [cursor, setCursor] = useState<string | null>(nextCursor);
  const [loadingMore, setLoadingMore] = useState(false);

  useEffect(() => {
    setRows(requests);
    setCursor(nextCursor);
  }, [requests, nextCursor]);

  const requestRows = useMemo(
    () => rows.map((request) => ({ request, outcomeLabel: tRequests(`outcomes.${request.outcome}`) })),
    [rows, tRequests],
  );
  const { sorted, sort, toggleSort } = useTableSort(requestRows, requestSortKey);

  const selectedOutcome = searchParams.get('outcome');

  function commitParam(paramKey: string, value: string | null, options?: { replace?: boolean }): void {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);

        if (value === null || value === '') next.delete(paramKey);
        else next.set(paramKey, value);

        return next;
      },
      { replace: options?.replace ?? false },
    );
  }

  function toggleOutcome(outcome: ApiKeyRequestOutcomeDto): void {
    commitParam('outcome', selectedOutcome === outcome ? null : outcome);
  }

  async function loadMore(): Promise<void> {
    if (cursor === null || loadingMore) return;
    setLoadingMore(true);

    try {
      const result = await apiClient.adminApiCalls.list({
        query: {
          ...requestQueryFromParams(searchParams),
          apiKeyId: key.id,
          credentialClass: 'api-key',
          ...ALL_WORKSPACES_QUERY,
          cursor,
        },
      });

      if (result.ok) {
        setRows((prev) => [...prev, ...result.body.data]);
        setCursor(result.body.nextCursor);
      }
    } finally {
      setLoadingMore(false);
    }
  }

  return (
    <>
      <PageTopbar
        leading={
          <BreadcrumbLeading
            icon={KeyIcon}
            crumbs={[{ label: t('title'), to: '/workspaces/api-keys' }, { label: key.name }]}
          />
        }
        trailing={
          // Not gated on `active`: the presented status gained a derived
          // `expired` (IMM-601.1) whose STORED status is still `active`, so the
          // backend revokes and edits an expired key as before — only a revoked
          // key loses its actions.
          key.status !== 'revoked' ? (
            <div className="flex items-center gap-2">
              {/* Edit is hidden on a revoked key, mirroring „Widerrufen" — a
                  disabled sibling next to a vanishing one reads as a bug. It is
                  hidden on an admin API key for a different reason (IMM-556.3): a
                  admin API key is immutable for its whole life, so the backend
                  refuses the PATCH and offering the control would be a dead end.
                  Revoke and re-issue instead. */}
              {key.kind === 'workspace' && (
                <RequirePermission permission="backoffice:identity:api-key-update">
                  <Button size="sm" variant="ghost" onClick={() => setEditOpen(true)}>
                    {tEdit('cta')}
                  </Button>
                </RequirePermission>
              )}
              <RequirePermission permission="backoffice:identity:api-key-revoke">
                <Button size="sm" variant="danger" onClick={() => setConfirmOpen(true)}>
                  {tRevoke('cta')}
                </Button>
              </RequirePermission>
            </div>
          ) : null
        }
      />
      <ResourceHeader icon={KeyIcon} title={key.name} meta={t(`status.${key.status}`)} />
      <div className="space-y-8 px-6 pb-8">
        <section className="space-y-2">
          {reachSentence !== null && <p className="text-base text-text-secondary">{reachSentence}</p>}
          <dl className="grid grid-cols-[180px_1fr] gap-y-2 text-base">
            <dt className="text-text-tertiary">{tDetail('id-label')}</dt>
            <dd className="font-mono text-sm text-text-primary">{key.id}</dd>
            <dt className="text-text-tertiary">{tDetail('name-label')}</dt>
            <dd className="text-text-primary">{key.name}</dd>
            <dt className="text-text-tertiary">{tDetail('description-label')}</dt>
            <dd className="text-text-primary">{key.description ?? '—'}</dd>
            <dt className="text-text-tertiary">{tDetail('kind-label')}</dt>
            <dd className="text-text-primary">{t(`kind.${key.kind}`)}</dd>
            <dt className="text-text-tertiary">{tDetail('workspace-label')}</dt>
            <dd className="text-text-primary">{labelFor(key.workspaceName, key.workspaceId, t('scope.none'))}</dd>
            <dt className="text-text-tertiary">{tDetail('member-label')}</dt>
            <dd className="text-text-primary">{labelFor(key.memberEmail, key.memberId, t('scope.none'))}</dd>
            <dt className="text-text-tertiary">{tDetail('scope-label')}</dt>
            <dd className="text-text-primary">
              {key.scope === null ? (
                t('scope.none')
              ) : key.scope.kind === 'ALL' ? (
                t('scope.all')
              ) : (
                // Every scoped workspace is NAMED, not counted: the detail is
                // where an operator answers "which workspaces can this consumer
                // see", and a count cannot answer it.
                <ul className="space-y-0.5">
                  {key.scopedWorkspaces.map((workspace) => (
                    <li key={workspace.id}>{workspace.name}</li>
                  ))}
                </ul>
              )}
            </dd>
            <dt className="text-text-tertiary">{tDetail('status-label')}</dt>
            <dd className="text-text-primary">{t(`status.${key.status}`)}</dd>
            <dt className="text-text-tertiary">{tDetail('rate-limit-label')}</dt>
            <dd className="text-text-primary tabular-nums">{tDetail('rate-limit-value', { count: key.rateLimit })}</dd>
            <dt className="text-text-tertiary">{tDetail('permissions-label')}</dt>
            {/* One row per permission, label then literal (IMM-620.1). This
                field used to be the bare comma-joined literals: correct for an
                operator matching a token against the API docs, unreadable for
                one checking WHAT a key may do. Both readings are now on the
                page, and the literal keeps the monospace that makes it scan as
                an identifier. */}
            <dd className="text-sm text-text-primary">
              {key.grantedPermissions.length === 0 ? (
                '—'
              ) : (
                <ul className="space-y-0.5">
                  {key.grantedPermissions.map((permission) => {
                    const identifier = permissionIdentifier(permission);

                    return (
                      <li key={permission}>
                        <span>{permissionLabel(permission)}</span>
                        {identifier !== null && (
                          <code className="ml-2 font-mono text-sm text-text-tertiary">{identifier}</code>
                        )}
                      </li>
                    );
                  })}
                </ul>
              )}
            </dd>
            <dt className="text-text-tertiary">{tDetail('issued-by-label')}</dt>
            <dd className="text-text-primary">{issuedByLabel}</dd>
            <dt className="text-text-tertiary">{tDetail('last-used-label')}</dt>
            <dd className="text-text-primary">
              {key.lastUsedAt === null
                ? t('never')
                : intl.formatDate(key.lastUsedAt, { dateStyle: 'long', timeStyle: 'medium' })}
            </dd>
            <dt className="text-text-tertiary">{tDetail('revoked-at-label')}</dt>
            <dd className="text-text-primary">
              {key.revokedAt === null
                ? '—'
                : intl.formatDate(key.revokedAt, { dateStyle: 'long', timeStyle: 'medium' })}
            </dd>
            {revokedByLabel !== null && (
              <>
                <dt className="text-text-tertiary">{tDetail('revoked-by-label')}</dt>
                <dd className="text-text-primary">{revokedByLabel}</dd>
              </>
            )}
            <dt className="text-text-tertiary">{tDetail('created-at-label')}</dt>
            <dd className="text-text-primary">{intl.formatDate(key.createdAt, { dateStyle: 'long' })}</dd>
          </dl>
        </section>

        <section className="space-y-3">
          <SectionHeading>{tDetail('requests-heading')}</SectionHeading>
          <FilterBar
            pills={OUTCOMES.map((outcome) => (
              <FilterPill
                key={outcome}
                icon={TagIcon}
                label={tRequests(`outcomes.${outcome}`)}
                active={selectedOutcome === outcome}
                onToggle={() => toggleOutcome(outcome)}
              />
            ))}
            searchValue={searchParams.get('endpoint') ?? ''}
            searchPlaceholder={tRequests('filters.endpoint-placeholder')}
            onSearchChange={(next) => commitParam('endpoint', next, { replace: true })}
          />
          <div className="flex flex-wrap items-end gap-3">
            <label className="flex flex-col gap-1 text-sm text-text-tertiary">
              {tRequests('filters.from')}
              <CommitOnBlurInput
                type="datetime-local"
                className="h-8"
                aria-label={tRequests('filters.from')}
                value={isoToLocalInput(searchParams.get('from'))}
                onCommit={(next) => commitParam('from', toIso(next), { replace: true })}
              />
            </label>
            <label className="flex flex-col gap-1 text-sm text-text-tertiary">
              {tRequests('filters.to')}
              <CommitOnBlurInput
                type="datetime-local"
                className="h-8"
                aria-label={tRequests('filters.to')}
                value={isoToLocalInput(searchParams.get('to'))}
                onCommit={(next) => commitParam('to', toIso(next), { replace: true })}
              />
            </label>
          </div>
          {rows.length === 0 ? (
            <EmptyState title={tDetail('requests-empty')} />
          ) : (
            <>
              <Table flush>
                <TableHeader>
                  <TableRow>
                    {REQUEST_HEADS.map((head) => (
                      <TableHead
                        key={head.field}
                        className={head.className}
                        {...(sort !== null && sort.field === head.field
                          ? { 'aria-sort': ARIA_SORT[sort.direction] }
                          : {})}
                      >
                        <SortableHeader
                          field={head.field}
                          label={<ColumnLabel icon={head.icon}>{tRequests(`columns.${head.labelKey}`)}</ColumnLabel>}
                          ariaLabel={tTable('sort-label', { column: tRequests(`columns.${head.labelKey}`) })}
                          sort={sort}
                          onSort={toggleSort}
                        />
                      </TableHead>
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sorted.map(({ request: row, outcomeLabel }) => (
                    <TableRow key={row.id} interactive onClick={() => navigate(`/logs/api-key-requests/${row.id}`)}>
                      <TableCell className="text-text-tertiary tabular-nums">
                        {intl.formatDate(row.occurredAt, { dateStyle: 'short', timeStyle: 'short' })}
                      </TableCell>
                      <TableCell className="font-mono text-sm">
                        <span className="text-text-tertiary">{row.method}</span> {row.endpoint}
                      </TableCell>
                      <TableCell className="tabular-nums">{row.statusCode}</TableCell>
                      <TableCell>
                        {outcomeLabel}
                        {/* Last-run status (IMM-458). The row's own status stays
                            the original call forever, so without this a recovered
                            entry is indistinguishable from an open failure while
                            working the queue. */}
                        {row.lastReplay !== null && (
                          <span className="ml-2 text-text-tertiary">
                            {row.lastReplay.outcome === null
                              ? `· ${tReplay('in-flight')}`
                              : `· ${tReplay('mark')} ${row.lastReplay.statusCode ?? ''}`}
                          </span>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              {cursor !== null && (
                <div className="flex justify-center p-4">
                  <button
                    type="button"
                    onClick={loadMore}
                    disabled={loadingMore}
                    className="inline-flex h-8 items-center gap-1 rounded-sm border border-border bg-surface px-3 text-base-sm font-medium text-text-secondary transition-colors duration-micro ease-out hover:bg-hover-bg focus-visible:outline-none focus-visible:shadow-focus disabled:opacity-60"
                  >
                    {loadingMore ? tDetail('load-more-loading') : tDetail('load-more')}
                  </button>
                </div>
              )}
            </>
          )}
        </section>
      </div>
      {confirmOpen && (
        <Modal
          open
          onClose={closeConfirm}
          confirmOnClose={false}
          size="md"
          title={tRevoke('confirm-title')}
          footer={
            <>
              <Button variant="ghost" type="button" onClick={closeConfirm} disabled={revoking}>
                {tRevoke('cancel-cta')}
              </Button>
              <Button type="button" variant="danger" onClick={confirmRevoke} disabled={revoking}>
                {revoking ? tRevoke('loading') : tRevoke('confirm-cta')}
              </Button>
            </>
          }
        >
          <p className="text-sm text-text-secondary">{tRevoke('confirm-body', { name: key.name })}</p>
          {revokeError !== null && <p className="mt-3 text-sm text-danger-600">{revokeError}</p>}
        </Modal>
      )}
      {editOpen && (
        <EditApiKeyDialog
          apiKey={key}
          memberLabel={key.memberEmail ?? t('unknown')}
          onClose={() => setEditOpen(false)}
          onSaved={() => {
            setEditOpen(false);
            // Revalidate rather than patch local state, so every derived field
            // on the page reflects the server's response.
            revalidator.revalidate();
          }}
        />
      )}
    </>
  );
}
